import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import { css } from '@emotion/css';
import { createTransition, motionDurations } from '../theme/motion';
import ActionButton from './ActionButton';

interface CatalogJobPollingStatusProps {
  isPolling: boolean;
  pollCount: number;
  pollLimitReached: boolean;
  isCheckingJobStatus: boolean;
  showDivider?: boolean;
  onRefreshNow: () => void;
  onContinue: () => void;
  onCheckJobStatus: () => void;
  onStop: () => void;
}

export default function CatalogJobPollingStatus({
  isPolling,
  pollCount,
  pollLimitReached,
  isCheckingJobStatus,
  showDivider = false,
  onRefreshNow,
  onContinue,
  onCheckJobStatus,
  onStop,
}: CatalogJobPollingStatusProps) {
  const meridianTheme = useMeridianTheme();

  if (!isPolling && !pollLimitReached) return null;

  const refreshLinkClass = css({
    padding: 0,
    border: 0,
    background: 'none',
    color: meridianTheme('themeActionPrimaryDefault'),
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: createTransition(['color'], motionDurations.instant),
    '&:hover': {
      color: meridianTheme('themeActionPrimaryHover'),
      textDecoration: 'underline',
    },
    '&:focus-visible': {
      outline: `2px solid ${meridianTheme('themeBorderFocus')}`,
      outlineOffset: 2,
      borderRadius: 4,
    },
  });

  return (
    <Column spacing="200">
      {showDivider && <Divider />}
      {isPolling ? (
        <Row spacing="200" alignmentVertical="center" wrap="down">
          <Text type="b200" color="secondary">
            Generating MCP specs. This page updates automatically when the job finishes.
          </Text>
          <button type="button" className={refreshLinkClass} onClick={onRefreshNow}>
            Check now
          </button>
        </Row>
      ) : (
        <>
          <Alert type="warning" size="medium">
            The job is still pending after {pollCount} checks. Check whether the job is still running, or continue
            polling.
          </Alert>
          <Row spacing="200" wrap="down">
            <ActionButton
              variant="primary"
              onClick={onCheckJobStatus}
              busy={isCheckingJobStatus}
              disabled={isCheckingJobStatus}
            >
              {isCheckingJobStatus ? 'Checking...' : 'Check job status'}
            </ActionButton>
            <ActionButton variant="secondary" onClick={onContinue}>
              Continue polling
            </ActionButton>
            <ActionButton variant="secondary" onClick={onStop}>
              Stop polling
            </ActionButton>
          </Row>
        </>
      )}
    </Column>
  );
}
